import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContentUltraPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { PlanoCNPJ } from '@/constants/planosCNPJ';
import { cn } from '@/lib/utils';

export interface LinhaComparativoPlano {
  contaNumero?: string;
  competenciaFatura?: string;
  id: string;
  numero: string;
  planoAtual: string;
  gbAtual: number;
  valorAtual: number;
  planoId: string | null;
  plano?: PlanoCNPJ;
  valorNovo: number;
  delta: number | null;
  pacoteValido?: boolean;
  faixa?: number | null;
  percentualComissao?: number | null;
  comissao?: number | null;
  tempoContratoMeses?: number | null;
}

interface Props {
  titulo: string;
  linhas: LinhaComparativoPlano[];
  planos: PlanoCNPJ[];
  mostrarComissao?: boolean;
  cabecalhoDireita?: ReactNode;
  restricaoGbMinimo?: boolean;
  onPlanoChange: (linhaId: string, plano: PlanoCNPJ) => void;
  onValorChange?: (linhaId: string, valor: number) => void;
}

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtLinhaTelefone = (valor: string) => {
  const digits = String(valor || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return valor;
};

const partirPlanoAtual = (nome: string) => {
  const match = String(nome || '').match(/^(.*?)\s*\((?:componentes:\s*)?([^)]*)\)\s*$/i);
  if (!match) return { nome: nome || '', componentes: null as string | null };
  return { nome: match[1].trim(), componentes: match[2].replace(/;\s*/g, ', ').trim() || null };
};

const deltaPercentual = (delta: number | null) => {
  if (delta === null) return '—';
  return `${delta > 0 ? '+' : ''}${delta.toFixed(2).replace('.', ',')}%`;
};

const corVariacao = (valor: number | null) => valor === null || valor === 0
  ? 'text-foreground'
  : valor > 0
    ? 'text-[#075E54] dark:text-[#25D366]'
    : 'text-primary dark:text-[#C87484]';

export function ComparativoLinhasPlano({
  titulo,
  linhas,
  planos,
  mostrarComissao = false,
  cabecalhoDireita,
  restricaoGbMinimo = false,
  onPlanoChange,
  onValorChange,
}: Props) {
  const grid = mostrarComissao
    ? 'grid grid-cols-[112px_minmax(145px,1.35fr)_58px_100px_88px_minmax(165px,1.1fr)_88px_78px_106px] gap-[8px] px-[14px]'
    : 'grid grid-cols-[112px_minmax(145px,1.25fr)_58px_100px_88px_minmax(175px,1.15fr)_88px_88px] gap-[8px] px-[14px]';
  const totalAtual = linhas.reduce((soma, linha) => soma + linha.valorAtual, 0);
  const totalNovo = linhas.reduce((soma, linha) => soma + linha.valorNovo, 0);
  const totalGbAtual = linhas.reduce((soma, linha) => soma + Number(linha.gbAtual || 0), 0);
  const selecaoCompleta = linhas.length > 0 && linhas.every((linha) => Boolean(linha.planoId));
  const totalGbNovo = selecaoCompleta
    ? linhas.reduce((soma, linha) => soma + Number(linha.plano?.gb || 0), 0)
    : null;
  const diferenca = selecaoCompleta ? totalNovo - totalAtual : null;
  const comissaoTotal = mostrarComissao && linhas.length > 0 && linhas.every((linha) => linha.comissao !== null && linha.comissao !== undefined)
    ? linhas.reduce((soma, linha) => soma + Number(linha.comissao), 0)
    : null;
  const deltaTotal = selecaoCompleta && totalAtual > 0 ? ((totalNovo - totalAtual) / totalAtual) * 100 : null;

  const planosDaLinha = (linha: LinhaComparativoPlano) => {
    if (!linha.plano || planos.some((plano) => plano.id === linha.plano?.id)) return planos;
    return [linha.plano, ...planos];
  };

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-[14px] py-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">{titulo}</p>
          {cabecalhoDireita}
        </div>
        <div className="max-h-[min(396px,44vh)] overflow-y-auto overflow-x-hidden">
          <div className={cn(grid, 'sticky top-0 z-10 h-8 items-center border-b border-border bg-muted text-[8.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground')}>
            <span>Linha</span><span>Plano atual</span><span className="text-center">TM</span><span className="text-center">GB atual → novo</span><span className="text-right">Valor atual</span><span>Plano novo</span><span className="text-right">Valor novo</span><span className="text-right">Delta</span>{mostrarComissao && <span className="text-right">Faixa · comissão</span>}
          </div>
          {linhas.map((linha, index) => {
            const planoAtual = partirPlanoAtual(linha.planoAtual);
            const sub = planoAtual.componentes
              ? `componentes: ${planoAtual.componentes}`
              : linha.gbAtual > 0 ? `${linha.gbAtual}GB${restricaoGbMinimo ? ` · mínimo ${linha.gbAtual}GB` : ''}` : '';
            return (
              <div key={linha.id} className={cn(grid, 'h-11 w-full items-center overflow-hidden border-b border-border text-left text-xs tabular-nums hover:bg-muted/55', index % 2 === 1 && 'bg-muted/25')}>
                <span className="truncate text-left font-semibold tabular-nums" title={linha.numero}>
                  {fmtLinhaTelefone(linha.numero)}
                  {linha.contaNumero && <span className="block text-[10px] font-normal text-muted-foreground" title={`Conta ${linha.contaNumero} · ${linha.competenciaFatura || ''}`}>Conta {linha.contaNumero} · {linha.competenciaFatura}</span>}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-xs font-semibold text-foreground" title={linha.planoAtual}>{planoAtual.nome || 'Não informado'}</span>
                  {sub && <span className="block truncate text-[10.5px] text-muted-foreground" title={sub}>{sub}</span>}
                </span>
                <span className="text-center text-[10.5px] font-bold tabular-nums text-foreground" title="Tempo médio das linhas na carteira Claro importada">
                  {linha.tempoContratoMeses == null ? '—' : `M${linha.tempoContratoMeses}`}
                </span>
                <span className="text-center text-[10.5px] font-semibold tabular-nums text-foreground" title="Franquia atual comparada à franquia do plano selecionado">
                  {linha.gbAtual > 0 ? `${linha.gbAtual} GB` : '—'} <span className="text-muted-foreground">→</span> {linha.planoId && linha.plano ? `${Number(linha.plano.gb || 0)} GB` : '—'}
                </span>
                <span className="truncate text-right text-primary">{moeda.format(linha.valorAtual)}</span>
                <Select value={linha.planoId || undefined} onValueChange={(planoId) => {
                  const plano = planosDaLinha(linha).find((opcao) => opcao.id === planoId);
                  if (plano) onPlanoChange(linha.id, plano);
                }}>
                  <SelectTrigger className="h-7 min-w-0 px-2 text-xs"><SelectValue placeholder="Selecione o plano novo" /></SelectTrigger>
                  <SelectContentUltraPriority>
                    {planosDaLinha(linha).map((plano) => {
                      const bloqueado = restricaoGbMinimo && Number(plano.gb || 0) < linha.gbAtual;
                      return <SelectItem key={plano.id} value={plano.id} disabled={bloqueado}>{plano.nome} · {moeda.format(plano.valor)}{bloqueado ? ` · abaixo do pacote atual (${linha.gbAtual}GB)` : ''}</SelectItem>;
                    })}
                  </SelectContentUltraPriority>
                </Select>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={linha.planoId ? linha.valorNovo : ''}
                  disabled={!linha.planoId || !onValorChange}
                  onChange={(event) => onValorChange?.(linha.id, Number(event.target.value) || 0)}
                  className="h-7 min-w-0 px-2 text-right text-xs font-bold tabular-nums text-primary"
                  aria-label={`Valor novo da linha ${linha.numero}`}
                />
                <span className={cn('truncate text-right font-semibold', !linha.planoId || linha.delta === null ? 'text-muted-foreground' : corVariacao(linha.delta))} title={!linha.planoId ? 'Selecione o plano novo' : linha.delta === null ? 'Valor atual inválido' : deltaPercentual(linha.delta)}>{linha.planoId ? deltaPercentual(linha.delta) : '—'}</span>
                {mostrarComissao && (
                  linha.pacoteValido === false
                    ? <span className="h-3 rounded-[2px] bg-[repeating-linear-gradient(135deg,#5B0A0D_0px,#5B0A0D_3px,#B4808C_3px,#B4808C_6px)] dark:bg-[repeating-linear-gradient(135deg,#C24545_0px,#C24545_3px,#5E2229_3px,#5E2229_6px)]" title="Sem plano compatível com o pacote atual" />
                    : linha.faixa != null
                      ? <span className="min-w-0 text-right leading-tight"><span className="block truncate text-[11px] font-bold tabular-nums text-foreground">F{linha.faixa}{linha.percentualComissao != null && <span className="font-semibold text-muted-foreground"> · {linha.percentualComissao}%</span>}</span>{linha.comissao != null && <span className="block truncate text-[10.5px] font-bold tabular-nums text-primary">{moeda.format(linha.comissao)}</span>}</span>
                      : <span className="truncate text-right text-muted-foreground/60">—</span>
                )}
              </div>
            );
          })}
        </div>
        <div className={cn(grid, 'h-9 items-center bg-muted text-xs font-bold tabular-nums')}>
          <span>TOTAL · {linhas.length}</span><span /><span /><span /><span className="text-right text-primary">{moeda.format(totalAtual)}</span><span /><span className="text-right text-primary">{selecaoCompleta ? moeda.format(totalNovo) : '—'}</span><span className={cn('truncate text-right font-bold', selecaoCompleta ? corVariacao(deltaTotal) : 'text-muted-foreground')}>{deltaPercentual(deltaTotal)}</span>{mostrarComissao && <span className="truncate text-right text-primary">{comissaoTotal == null ? '—' : moeda.format(comissaoTotal)}</span>}
        </div>
      </div>

      <div className={cn('grid grid-cols-2 gap-2', mostrarComissao ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
        {[
          { label: 'Linhas', value: String(linhas.length), detail: null },
          { label: 'Valor atual', value: moeda.format(totalAtual), detail: `${totalGbAtual.toLocaleString('pt-BR')} GB` },
          { label: 'Valor novo', value: selecaoCompleta ? moeda.format(totalNovo) : '—', detail: totalGbNovo == null ? 'GB —' : `${totalGbNovo.toLocaleString('pt-BR')} GB` },
          { label: 'Diferença mensal', value: diferenca === null ? '—' : `${diferenca > 0 ? '+' : ''}${moeda.format(diferenca)}`, detail: null },
          ...(mostrarComissao ? [{ label: 'Comissão estimada', value: comissaoTotal == null ? '—' : moeda.format(comissaoTotal), detail: null }] : []),
        ].map(({ label, value, detail }) => (
          <div key={label} className="rounded-lg border border-border bg-card px-3 py-1.5">
            <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
            <div className="mt-0.5 flex min-w-0 items-baseline gap-2">
              <p className={cn('truncate text-sm font-bold tabular-nums', label === 'Diferença mensal' ? corVariacao(diferenca) : value === '—' ? 'text-muted-foreground' : 'text-primary')} title={value}>{value}</p>
              {detail && <span className="shrink-0 text-[10.5px] font-bold tabular-nums text-foreground">{detail}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
